<script setup lang="ts">
import './assets/style.scss'
import { useRoute } from 'vue-router'
import { VIcons } from '@/shared/ui'
import { computed } from 'vue'
import { ReportTabsTypes } from '@/entities/Report'
import ReportRefetchButton from './ReportRefetchButton.vue'
import useModel from './../model/model'
import { useCabinetReports } from '@/pages/object/CabinetReports'
import ReportSaveButton from './ReportSaveButton.vue'
import ReportSendButton from './ReportSendButton.vue'
import { DateTime } from 'ts-luxon'
import ReportSubscribeButton from './ReportSubscribeButton.vue'

const route = useRoute()
const { timeIntervals } = useModel()
const model = useCabinetReports()

/** Вычисляем текущую вкладку */
const currentTab = computed(() => route.params.report_type as string)

/** Вычисляем показывать кнопку запросить у ШУНО */
const isRefetchCabinetBtn = computed(() => {
  // const tabs = [ReportTabsTypes.READINGS.id, ReportTabsTypes.CURRENT.id]
  const tabs = []

  return !(model.filter.meter === -1 && model.metersCount > 1) && tabs.includes(currentTab.value)
})

/** Список исключений для кнопок */
const exceptions = {
  save: [
    ReportTabsTypes.INVENT_ACT.id, 
    // ReportTabsTypes.GREEN_GLADE.id, 
    // ReportTabsTypes.TIME_SLICE.id, 
    // ReportTabsTypes.CURRENT.id
  ],
  email: [
    ReportTabsTypes.INVENT_ACT.id,
    // ReportTabsTypes.CONS_COMPARE.id,
    // ReportTabsTypes.GREEN_GLADE.id,
    // ReportTabsTypes.TIME_SLICE.id,
    // ReportTabsTypes.CURRENT.id,
  ],
  schedule: [
    ReportTabsTypes.INVENT_ACT.id,
    // ReportTabsTypes.CONS_COMPARE.id,
    // ReportTabsTypes.CURRENT.id,
    // ReportTabsTypes.INVENTORY.id,
    // ReportTabsTypes.GREEN_GLADE.id,
    // ReportTabsTypes.TIME_SLICE.id,
  ],
}

/** Список полей формы для вкладок */
const fieldsMap = {
  dates: [
    ReportTabsTypes.INVENT_ACT.id,
    // ReportTabsTypes.READINGS.id,
    // ReportTabsTypes.CONS_DAYS.id,
    // ReportTabsTypes.CONS_PERIOD.id,
    // ReportTabsTypes.CURRENT_ARCHIVE.id,
    // ReportTabsTypes.TIME_SLICE.id,
    // ReportTabsTypes.GREEN_GLADE.id,
  ],
  interval: [
    ReportTabsTypes.INVENT_ACT.id
    // ReportTabsTypes.TIME_SLICE.id
  ],
  compare: [
    ReportTabsTypes.INVENT_ACT.id
    // ReportTabsTypes.CONS_COMPARE.id
  ],
}

/** Список ограничений по времени */
const periodToType = {
  [ReportTabsTypes.INVENT_ACT.id]: { months: 12 },
  // [ReportTabsTypes.READINGS.id]: { months: 12 },
  // [ReportTabsTypes.CONS_DAYS.id]: { months: 12 },
  // [ReportTabsTypes.CONS_PERIOD.id]: {},
  // [ReportTabsTypes.CURRENT_ARCHIVE.id]: { months: 6 },
  // [ReportTabsTypes.TIME_SLICE.id]: { months: 2 },
  // [ReportTabsTypes.GREEN_GLADE.id]: { months: 2 },
}

/** Вычисляем максимально разрешенное время */
const maxDate = computed(() => {
  const fromDate = DateTime.fromFormat(model.filter.date_from, model.dateFormat)
  const period = periodToType[currentTab.value]
  let max = ''

  if (Object.keys(period).length) {
    const toDate = fromDate.plus(period).endOf('day')
    const now = DateTime.now()
    max = (toDate.toMillis() > now.toMillis() ? now : toDate).toFormat(model.dateFormat)
  } else {
    max = DateTime.now().toFormat(model.dateFormat)
  }

  model.filter.date_to = max
  return max
})

/** Вычисляем допустимые месяцы для первого промежутка */
const firstMonths = computed(
  () => model.timeCompare.first.find((item) => item.value == model.filter.year_first)?.months || [],
)

/** Вычисляем допустимые месяцы для второго промежутка */
const lastMonths = computed(
  () => model.timeCompare.first.find((item) => item.value == model.filter.year_last)?.months || [],
)
</script>

<template>
  <div class="report-form__wrapper">
    <div class="report-form__inputs">
      <template v-if="fieldsMap.dates.includes(currentTab)">
        <VTextField
          type="datetime-local"
          hide-details
          v-model="model.filter.date_from"
          :label="$t('itf.date_from')"
          :max="model.filter.date_to"
        />

        <VTextField
          type="datetime-local"
          hide-details
          v-model="model.filter.date_to"
          :label="$t('itf.date_to')"
          :max="maxDate"
        />
      </template>

      <template v-if="fieldsMap.interval.includes(currentTab)">
        <VSelect
          hide-details
          :items="timeIntervals"
          v-model="model.filter.interval"
          :label="$t('itf.interval')"
        />

        <div class="report-form__phase">
          {{ $t('itf.phase') }}:
          <VCheckbox hide-details label="A" v-model="model.filter.phase_a" />
          <VCheckbox hide-details label="B" v-model="model.filter.phase_b" />
          <VCheckbox hide-details label="C" v-model="model.filter.phase_c" />
        </div>
      </template>

      <template v-if="fieldsMap.compare.includes(currentTab)">
        <VSelect
          hide-details
          :label="$t('itf.year')"
          :items="model.timeCompare.first"
          v-model="model.filter.year_first"
        />

        <VSelect
          hide-details
          :label="$t('itf.moths')"
          :items="firstMonths"
          v-model="model.filter.month_first"
        />

        <span>-</span>

        <VSelect
          hide-details
          :label="$t('itf.year')"
          :items="model.timeCompare.last"
          v-model="model.filter.year_last"
        />

        <VSelect
          hide-details
          :label="$t('itf.moths')"
          :items="lastMonths"
          v-model="model.filter.month_last"
        />
      </template>

      <VTooltip :text="$t('itf.refresh')" location="top">
        <template #activator="{ props }">
          <VBtn color="warning" v-bind="props" @click="model.callback">
            <VIcons type="sync" />
          </VBtn>
        </template>
      </VTooltip>
    </div>

    <div class="report-form__buttons">
      <ReportSaveButton v-if="!exceptions.save.includes(currentTab)" />

      <ReportSendButton v-if="!exceptions.email.includes(currentTab)" />

      <ReportSubscribeButton v-if="!exceptions.schedule.includes(currentTab)" />

      <template v-if="isRefetchCabinetBtn">
        <ReportRefetchButton />
      </template>
    </div>
  </div>
</template>
